import axiosInstance from './api'

const EMPTY_RESEARCH_FIELDS = {
	category: null,
	subCategory: null,
	selectedPlan: null,
	career: null,
	countries: null,
	course: null,
	highestLevel: null,
}

const EMPTY_ONBOARDING_FIELDS = {
	category: null,
	subCategory: null,
	dreamCountry: null,
	plannedDate: null,
}

const normalizeResearchFields = data => ({
	...EMPTY_RESEARCH_FIELDS,
	...(data && typeof data === 'object' ? data : {}),
})

const normalizeOnboardingFields = data => ({
	...EMPTY_ONBOARDING_FIELDS,
	...(data && typeof data === 'object' ? data : {}),
})

const normalizeJourney = data => ({
	id: null,
	...EMPTY_RESEARCH_FIELDS,
	country: null,
	careers: null,
	courses: null,
	update_ts: null,
	isCreatedShortlist: false,
	typeFlowJourney: null,
	created_at: null,
	updated_at: null,
	...(data && typeof data === 'object' ? data : {}),
})

const withSignal = signal => (signal ? { signal } : undefined)

const requireStudentId = studentId => {
	const normalizedId = String(studentId || '').trim()
	if (!normalizedId) {
		throw new Error('Invalid studentId: empty or null')
	}
	return normalizedId
}

/** Personal Profile (Counselor-owned working state). */
export const fetchPersonalResearchDefaults = async ({ signal } = {}) => {
	const response = await axiosInstance.get(
		'/counseling/counselor/research/personal-defaults',
		withSignal(signal),
	)
	return normalizeResearchFields(response.data)
}

export const savePersonalResearchDefaults = async payload => {
	const response = await axiosInstance.put(
		'/counseling/counselor/research/personal-defaults',
		payload,
	)
	return normalizeResearchFields(response.data)
}

/** Counselor Student Profile (Counselor-owned). Never writes Student aitools. */
export const fetchStudentResearchProfile = async (studentId, { signal } = {}) => {
	const normalizedId = requireStudentId(studentId)
	const response = await axiosInstance.get(
		`/counseling/counselor/students/${encodeURIComponent(normalizedId)}/research-profile`,
		withSignal(signal),
	)
	return normalizeResearchFields(response.data)
}

export const saveStudentResearchProfile = async (studentId, payload) => {
	const normalizedId = requireStudentId(studentId)
	const response = await axiosInstance.put(
		`/counseling/counselor/students/${encodeURIComponent(normalizedId)}/research-profile`,
		payload,
	)
	return normalizeResearchFields(response.data)
}

/** @deprecated Use fetchStudentResearchProfile — alias kept for transition. */
export const fetchStudentResearchJourney = fetchStudentResearchProfile

/** @deprecated Use saveStudentResearchProfile — redirects to Counselor Student Profile. */
export const saveStudentResearchJourney = saveStudentResearchProfile

/** Student aitools_profile — READ ONLY fallback. */
export const fetchStudentAitoolsProfile = async (studentId, { signal } = {}) => {
	const normalizedId = requireStudentId(studentId)
	const response = await axiosInstance.get(
		`/counseling/counselor/students/${encodeURIComponent(normalizedId)}/aitools-profile`,
		withSignal(signal),
	)
	return normalizeResearchFields(response.data)
}

export const fetchStudentOnboardingProfile = async (studentId, { signal } = {}) => {
	const normalizedId = requireStudentId(studentId)
	const response = await axiosInstance.get(
		`/counseling/counselor/students/${encodeURIComponent(normalizedId)}/onboarding-profile`,
		withSignal(signal),
	)
	return normalizeOnboardingFields(response.data)
}

/** Counselor Personal Journey History */
export const listPersonalResearchJourneys = async ({ limit = 5, signal } = {}) => {
	const response = await axiosInstance.get(
		'/counseling/counselor/research/journeys',
		{ ...withSignal(signal), params: { limit } },
	)
	return Array.isArray(response.data)
		? response.data.map(normalizeJourney)
		: []
}

export const createPersonalResearchJourney = async payload => {
	const response = await axiosInstance.post(
		'/counseling/counselor/research/journeys',
		payload,
	)
	return normalizeJourney(response.data)
}

export const patchPersonalResearchJourney = async (journeyId, payload) => {
	const response = await axiosInstance.patch(
		`/counseling/counselor/research/journeys/${encodeURIComponent(journeyId)}`,
		payload,
	)
	return normalizeJourney(response.data)
}

export const deletePersonalResearchJourney = async journeyId => {
	await axiosInstance.delete(
		`/counseling/counselor/research/journeys/${encodeURIComponent(journeyId)}`,
	)
	return true
}

/** Counselor Student Journey History */
export const listStudentResearchJourneys = async (
	studentId,
	{ limit = 5, signal } = {},
) => {
	const normalizedId = requireStudentId(studentId)
	const response = await axiosInstance.get(
		`/counseling/counselor/students/${encodeURIComponent(normalizedId)}/research-journeys`,
		{ ...withSignal(signal), params: { limit } },
	)
	return Array.isArray(response.data)
		? response.data.map(normalizeJourney)
		: []
}

export const createStudentResearchJourney = async (studentId, payload) => {
	const normalizedId = requireStudentId(studentId)
	const response = await axiosInstance.post(
		`/counseling/counselor/students/${encodeURIComponent(normalizedId)}/research-journeys`,
		payload,
	)
	return normalizeJourney(response.data)
}

export const patchStudentResearchJourney = async (
	studentId,
	journeyId,
	payload,
) => {
	const normalizedId = requireStudentId(studentId)
	const response = await axiosInstance.patch(
		`/counseling/counselor/students/${encodeURIComponent(normalizedId)}/research-journeys/${encodeURIComponent(journeyId)}`,
		payload,
	)
	return normalizeJourney(response.data)
}

export const deleteStudentResearchJourney = async (studentId, journeyId) => {
	const normalizedId = requireStudentId(studentId)
	await axiosInstance.delete(
		`/counseling/counselor/students/${encodeURIComponent(normalizedId)}/research-journeys/${encodeURIComponent(journeyId)}`,
	)
	return true
}

/**
 * Student-owned aitools_journeys — READ ONLY for counselor.
 * Never PATCH/DELETE via this surface.
 */
export const listStudentAitoolsJourneys = async (
	studentId,
	{ limit = 5, signal } = {},
) => {
	const normalizedId = requireStudentId(studentId)
	const response = await axiosInstance.get(
		`/counseling/counselor/students/${encodeURIComponent(normalizedId)}/aitools-journeys`,
		{ ...withSignal(signal), params: { limit } },
	)
	return Array.isArray(response.data)
		? response.data.map(normalizeJourney)
		: []
}

